import React, { useState, useEffect } from 'react';
import BackWithSidebar from '../../../../components/BackWithSidebar';
import GeneralIcon from '../../../../assets';
import SearchBar from '../../../../components/SearchBar';
import ReturnsModal from '../../../../components/ReturnsModal';
import orderService, { ORDER_STATUSES, PRODUCTION_STATUSES } from '../../../../services/orderService';
import { getOrderStatusStyle, getProductionStatusStyle } from '../../../../constants/orderStatusStyles';
import { resolveThumbnail } from '../../../../models/orderModel';
import orderBusinessLogic from '../../../../utils/orderBusinessLogic';

// Empty state component
function EmptyState({ production = false }) {
	return (
		<div className="flex flex-col items-center justify-center py-16 text-center">
			<GeneralIcon iconName="vazio3" className="w-28 h-28 mb-4 opacity-60" />
			<p className="text-gray-500 text-sm">
				{production ? 'Nenhum item em produção.' : 'Nenhum pedido encontrado.'}
			</p>
		</div>
	);
}

// Skeleton loading component
function OrderCardSkeleton() {
	return (
		<div className="border rounded-xl p-4 bg-white animate-pulse">
			<div className="flex items-start justify-between mb-2">
				<div className="flex-1">
					<div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
					<div className="h-3 bg-gray-100 rounded w-24"></div>
				</div>
				<div className="h-3 bg-gray-200 rounded w-20"></div>
			</div>
			<div className="grid grid-cols-2 gap-3 mb-3">
				<div>
					<div className="h-3 bg-gray-100 rounded w-16 mb-1"></div>
					<div className="h-4 bg-gray-200 rounded w-24"></div>
				</div>
				<div className="text-right">
					<div className="h-3 bg-gray-100 rounded w-16 mb-1 ml-auto"></div>
					<div className="h-4 bg-gray-200 rounded w-12 ml-auto"></div>
				</div>
			</div>
			<div className="flex gap-1.5">
				{[1, 2, 3].map(i => (
					<div key={i} className="w-12 h-12 bg-gray-100 rounded-lg"></div>
				))}
			</div>
		</div>
	);
}

function ProductionItemSkeleton() {
	return (
		<div className="border rounded-xl p-4 bg-white animate-pulse">
			<div className="flex items-start justify-between mb-2">
				<div className="flex items-center gap-3 flex-1">
					<div className="w-10 h-10 bg-gray-100 rounded-lg"></div>
					<div className="flex-1">
						<div className="h-4 bg-gray-200 rounded w-40 mb-1"></div>
						<div className="h-3 bg-gray-100 rounded w-32 mb-1"></div>
						<div className="h-2 bg-gray-100 rounded w-24"></div>
					</div>
				</div>
				<div className="h-3 bg-gray-200 rounded w-20"></div>
			</div>
			<div className="h-3 bg-gray-100 rounded w-28 mb-3"></div>
			<div className="h-9 bg-gray-100 rounded-lg w-full"></div>
		</div>
	);
}

// Order card summary
function OrderCard({ order, onClick }) {
	const style = getOrderStatusStyle(order.status);
	return (
		<div
			onClick={() => onClick(order)}
			className="border rounded-xl p-4 bg-white hover:shadow-md transition cursor-pointer"
		>
			<div className="flex items-start justify-between mb-2">
				<div>
					<div className="font-semibold text-gray-800">Pedido {order.code}</div>
					<div className="text-xs text-gray-500 mt-0.5">{formatDateTime(order.createdAt)}</div>
				</div>
				<div className={`text-xs font-semibold ${style.text}`}>{order.status}</div>
			</div>
			<div className="grid grid-cols-2 gap-3 text-sm mb-3">
				<div>
					<div className="text-gray-500 text-xs">Valor total</div>
					<div className="text-gray-800 font-medium">R$ {order.total.toFixed(2)}</div>
				</div>
				<div className="text-right">
					<div className="text-gray-500 text-xs">Produtos</div>
					<div className="text-gray-800 font-medium">{order.productCount}</div>
				</div>
			</div>
			{order.productThumbs.length > 0 && (
				<div className="flex gap-1.5 overflow-x-auto">
					{order.productThumbs.map(p => (
						<ProductThumb key={p.id} product={p} />
					))}
				</div>
			)}
		</div>
	);
}

// Production item card
function ProductionItemCard({ item, onAdvance }) {
	const style = getProductionStatusStyle(item.productionStatus);
	return (
		<div className="border rounded-xl p-4 bg-white">
			<div className="flex items-start justify-between mb-2">
				<div>
					<div className="flex items-center gap-3">
						<ProductThumb product={item} size="sm" />
						<div>
							<div className="font-semibold text-gray-800 text-sm">Integrante do pedido {item.orderCode}</div>
							<div className="text-xs text-purple-600 font-medium truncate max-w-[160px]">{item.name}</div>
							<div className="text-[11px] text-gray-500">{item.variant}</div>
						</div>
					</div>
				</div>
				<div className={`text-xs font-semibold ${style.text}`}>{item.productionStatus}</div>
			</div>
			<div className="text-xs text-gray-600 mb-3">Quantidade {item.quantity}</div>
			<button
				onClick={() => onAdvance(item)}
				className={`w-full text-xs h-9 rounded-lg font-medium transition ${style.actionBg}`}
			>
				{item.productionStatus === 'PENDENTE' && 'Iniciar preparação'}
				{item.productionStatus === 'PREPARANDO' && 'Marcar como pronto'}
				{item.productionStatus === 'PRONTO' && 'Pronto'}
			</button>
		</div>
	);
}

// Order details modal
function OrderDetailsModal({ order, onClose, onUpdateItem, onCancelOrder }) {
	const [expandedProductId, setExpandedProductId] = useState(null);
	const [showReturnsModal, setShowReturnsModal] = useState(false);
	if (!order) return null;
	const style = getOrderStatusStyle(order.status);

	return (
		<div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 backdrop-blur-sm p-6">
			<div className="bg-white w-full max-w-xl rounded-2xl shadow-lg">
				<div className="flex items-center justify-between px-5 py-4 border-b">
					<div className="flex items-center gap-2">
						<button
							onClick={onClose}
							className="p-2 -ml-2 rounded hover:bg-gray-100 transition-colors"
							title="Voltar"
						>
							←
						</button>
						<h2 className="font-semibold text-gray-800">Pedido {order.code}</h2>
					</div>
					<div className={`text-xs font-semibold ${style.text}`}>{order.status}</div>
				</div>
				<div className="p-5 space-y-6 max-h-[80vh] overflow-y-auto">
					<section>
						<h3 className="text-sm font-semibold text-gray-700 mb-2">Sobre o pedido</h3>
						<div className="grid grid-cols-2 gap-3 text-xs">
							<div>
								<div className="text-gray-500">Data de criação</div>
								<div className="text-gray-800 font-medium">{formatDateTime(order.createdAt)}</div>
							</div>
							<div>
								<div className="text-gray-500">Tipo de produto</div>
								<div className="text-gray-800 font-medium">{order.type}</div>
							</div>
							<div>
								<div className="text-gray-500">Status</div>
								<div className={`font-semibold ${style.text}`}>{order.status}</div>
							</div>
						</div>
					</section>
					<section>
						<h3 className="text-sm font-semibold text-gray-700 mb-2">Pagamento</h3>
						<div className="grid grid-cols-2 gap-3 text-xs">
							<div>
								<div className="text-gray-500">Ambiente</div>
								<div className="text-gray-800 font-medium">{order.payment.environment}</div>
							</div>
							<div>
								<div className="text-gray-500">Método</div>
								<div className="text-gray-800 font-medium">{order.payment.method}</div>
							</div>
							<div>
								<div className="text-gray-500">Data do pagamento</div>
								<div className="text-gray-800 font-medium">{formatDateTime(order.payment.paidAt)}</div>
							</div>
							<div>
								<div className="text-gray-500">Valor pago</div>
								<div className="text-gray-800 font-medium">R$ {order.payment.amountPaid.toFixed(2)}</div>
							</div>
							<div>
								<div className="text-gray-500">Troco</div>
								<div className="text-gray-800 font-medium">R$ {order.payment.change.toFixed(2)}</div>
							</div>
							<div>
								<div className="text-gray-500">Código</div>
								<div className="flex items-center gap-2">
									<div className="text-gray-800 font-medium">{order.payment.paymentCode}</div>
								</div>
							</div>
						</div>
						{order.payment.paidAt && order.payment.receiptUrl && (
							<div className="mt-3">
								<a
									href={order.payment.receiptUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
								>
									<GeneralIcon iconName="documento" className="w-4 h-4" />
									Visualizar comprovante
								</a>
							</div>
						)}
					</section>
					<section>
						<h3 className="text-sm font-semibold text-gray-700 mb-2">Resumo do pedido</h3>
						<div className="grid grid-cols-2 gap-3 text-xs">
							<div>
								<div className="text-gray-500">Subtotal</div>
								<div className="text-gray-800 font-medium">R$ {order.pricing.subtotal.toFixed(2)}</div>
							</div>
							<div>
								<div className="text-gray-500">Entrega</div>
								<div className="text-gray-800 font-medium">{order.pricing.shipping === 0 ? 'Grátis' : `R$ ${order.pricing.shipping.toFixed(2)}`}</div>
							</div>
							<div>
								<div className="text-gray-500">Descontos</div>
								<div className="text-gray-800 font-medium">R$ {order.pricing.discounts.toFixed(2)}</div>
							</div>
							<div>
								<div className="text-gray-500">Total</div>
								<div className="text-gray-800 font-semibold">R$ {order.pricing.total.toFixed(2)}</div>
							</div>
						</div>
					</section>
					<section>
						<h3 className="text-sm font-semibold text-gray-700 mb-2">Produtos</h3>
						<div className="space-y-3">
							{order.products.map(p => {
								const displayStatus = orderBusinessLogic.getProductDisplayStatus(order, p);
								const prodStyle = getProductionStatusStyle(displayStatus);
								const expanded = expandedProductId === p.id;
								return (
									<div key={p.id} className="border rounded-xl overflow-hidden">
										<button
											onClick={() => setExpandedProductId(expanded ? null : p.id)}
											className="w-full flex items-start justify-between p-3 text-left hover:bg-gray-50"
										>
											<div className="flex items-center gap-3">
												<ProductThumb product={p} />
												<div>
													<div className="text-sm font-semibold text-gray-800">{p.name}</div>
													<div className="text-[11px] text-purple-600 font-medium">{p.variant}</div>
												</div>
											</div>
											<div className={`text-xs font-semibold ${prodStyle.text}`}>{displayStatus}</div>
										</button>
										{expanded && (
											<div className="px-4 pb-4 text-xs space-y-3">
												<div>
													<div className="text-gray-500 font-semibold mb-2">Status</div>
													<div className={`inline-block px-3 py-1.5 rounded-lg text-xs font-semibold ${prodStyle.badge} ${prodStyle.text}`}>
														{displayStatus}
													</div>
												</div>
												<div className="grid grid-cols-2 gap-3">
													<Info label="Cor" value={p.color} />
													<Info label="Estilo" value={p.style} />
													<Info label="Peso" value={p.weight} />
													<Info label="Tipo" value={p.type} />
													<Info label="Valor unitário" value={`R$ ${p.unitPrice.toFixed(2)}`} />
													<Info label="Quantidade" value={p.quantity} />
													<Info label="Subtotal" value={`R$ ${(p.unitPrice * p.quantity).toFixed(2)}`} />
													<Info label="Descontos" value={`R$ ${p.discountAmount.toFixed(2)}`} />
													<Info label="Total" value={`R$ ${(p.unitPrice * p.quantity - p.discountAmount).toFixed(2)}`} />
												</div>
												{displayStatus !== 'PRONTO' && displayStatus !== 'ENTREGUE' && displayStatus !== 'DEVOLVIDO' && (() => {
													const canStart = orderBusinessLogic.canStartProduction(order);
													const disabled = p.productionStatus === 'PENDENTE' && !canStart;
													return (
														<button
															onClick={(e) => {
																e.stopPropagation();
																onUpdateItem({ ...p, orderId: order.id });
															}}
															disabled={disabled}
															title={disabled ? 'Aguardando confirmação de pagamento' : ''}
															className={`w-full text-xs h-9 rounded-lg font-medium transition ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : prodStyle.actionBg}`}
														>
															{p.productionStatus === 'PENDENTE' && 'Iniciar preparação'}
															{p.productionStatus === 'PREPARANDO' && 'Marcar como pronto'}
														</button>
													);
												})()}
											</div>
										)}
									</div>
								);
							})}
						</div>
					</section>
					<section>
						<h3 className="text-sm font-semibold text-gray-700 mb-2">Informações de entrega</h3>
						<div className="grid grid-cols-2 gap-3 text-xs">
							<Info label="Método de entrega" value={order.delivery.method} />
							<Info label="Data máxima para a coleta" value={formatDate(order.delivery.maxPickupDate)} />
						</div>
						{order.returns && order.returns.length > 0 && (
							<div className="mt-3">
								<button
									onClick={(e) => {
										e.stopPropagation();
										setShowReturnsModal(true);
									}}
									className="inline-flex items-center gap-2 text-xs text-purple-600 hover:text-purple-700 font-medium"
								>
									<GeneralIcon iconName="caixa" className="w-4 h-4" />
									Ver devoluções ({order.returns.length})
								</button>
							</div>
						)}
					</section>
					{order.invoice && order.invoice.url && (
						<section>
							<h3 className="text-sm font-semibold text-gray-700 mb-2">Nota fiscal</h3>
							<a
								href={order.invoice.url}
								download={order.invoice.filename || 'nota_fiscal.pdf'}
								className="flex items-center gap-3 p-3 rounded-lg bg-purple-50 hover:bg-purple-100 transition border border-purple-200"
							>
								<div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100">
									<span className="text-purple-600 text-xs font-semibold">PDF</span>
								</div>
								<div className="flex-1 text-xs">
									<div className="text-gray-800 font-medium">{order.invoice.filename || 'nota_fiscal.pdf'}</div>
								</div>
								<GeneralIcon iconName="baixar" className="w-5 h-5 text-purple-600" />
							</a>
						</section>
					)}
					<div>
						<button
							onClick={() => onCancelOrder?.(order)}
							className="w-full h-11 rounded-lg bg-red-50 text-red-600 text-sm font-medium"
						>
							Cancelar o pedido
						</button>
					</div>
				</div>
			</div>
			<ReturnsModal 
				isOpen={showReturnsModal} 
				onClose={() => setShowReturnsModal(false)} 
				order={order} 
			/>
		</div>
	);
}

function Info({ label, value }) {
	return (
		<div>
			<div className="text-gray-500">{label}</div>
			<div className="text-gray-800 font-medium">{value}</div>
		</div>
	);
}

// Product thumbnail component: resolves key-based storage or uses direct URL.
function ProductThumb({ product, size = 'md' }) {
	const [url, setUrl] = React.useState(null);
	const dimension = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12';

	React.useEffect(() => {
		let active = true;
		async function load() {
			if (!product.thumbnail) {
				setUrl(null);
				return;
			}
			if (product.thumbnail.url) {
				setUrl(product.thumbnail.url);
			} else if (product.thumbnail.key) {
				const resolved = await resolveThumbnail(product.thumbnail);
				if (active) setUrl(resolved);
			}
		}
		load();
		return () => {
			active = false;
		};
	}, [product.thumbnail]);

	return (
		<div className={`${dimension} rounded-lg border bg-gray-50 flex items-center justify-center overflow-hidden`}>      
			{url ? (
				<img src={url} alt={product.name} className="object-cover w-full h-full" />
			) : (
				<span className="text-[10px] text-gray-500 px-1 text-center leading-tight">
					{product.name.split(' ')[0]}
				</span>
			)}
		</div>
	);
}

function formatDate(iso) {
	const d = new Date(iso);
	return d.toLocaleDateString('pt-BR');
}

function formatDateTime(iso) {
	const d = new Date(iso);
	return `${d.toLocaleDateString('pt-BR')} - ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function Pedidos() {
	const [activeTab, setActiveTab] = useState('PEDIDOS');
	const [loading, setLoading] = useState(false);
	const [orders, setOrders] = useState([]);
	const [productionItems, setProductionItems] = useState([]);
	const [selectedOrder, setSelectedOrder] = useState(null);

	// Filters
	const [search, setSearch] = useState('');
	const [orderStatusFilters, setOrderStatusFilters] = useState([]);
	const [prodStatusFilters, setProdStatusFilters] = useState([]);
	const [filtersMenuOpen, setFiltersMenuOpen] = useState(false);
	
	// Debug mode & fallback indicator
	const [showDebug, setShowDebug] = useState(false);
	const [isUsingFallback, setIsUsingFallback] = useState(false);
	const [error, setError] = useState(null);

	const loadData = React.useCallback(async () => {
		setLoading(true);
		setIsUsingFallback(false);
		setError(null);
		try {
			if (activeTab === 'PEDIDOS') {
				try {
					const data = await orderService.fetchOrders({ statuses: orderStatusFilters, search });
					setOrders(data);
				} catch (err) {
					setIsUsingFallback(true);
					// Retry with explicit fallback awareness
					const data = await orderService.fetchOrders({ statuses: orderStatusFilters, search });
					setOrders(data);
				}
			} else {
				try {
					const items = await orderService.fetchProductionItems({ statuses: prodStatusFilters });
					setProductionItems(items);
				} catch (err) {
					setIsUsingFallback(true);
					const items = await orderService.fetchProductionItems({ statuses: prodStatusFilters });
					setProductionItems(items);
				}
			}
		} catch (err) {
			console.error('Error loading data:', err);
			setError('Erro ao carregar dados. Usando dados locais.');
			setIsUsingFallback(true);
		} finally {
			setLoading(false);
		}
	}, [activeTab, orderStatusFilters, prodStatusFilters, search]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	function toggleOrderStatus(status) {
		setOrderStatusFilters(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
	}
	function toggleProdStatus(status) {
		setProdStatusFilters(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
	}

	function clearFilters() {
		setOrderStatusFilters([]);
		setProdStatusFilters([]);
	}

	async function handleAdvanceProduction(item) {
		// Check if production can start (payment must be confirmed)
		if (item.productionStatus === 'PENDENTE') {
			const order = await orderService.fetchOrderDetails(item.orderId);
			if (!orderBusinessLogic.canStartProduction(order)) {
				alert('Aguardando confirmação de pagamento. Não é possível iniciar a preparação.');
				return;
			}
		}
		
		setLoading(true);
		try {
			const next = item.productionStatus === 'PENDENTE' ? 'PREPARANDO' : item.productionStatus === 'PREPARANDO' ? 'PRONTO' : 'PRONTO';
			await orderService.updateProductionItemStatus(item.orderId, item.id, next);
			await loadData();
			if (selectedOrder) {
				const full = await orderService.fetchOrderDetails(selectedOrder.id);
				setSelectedOrder(full);
			}
		} catch (err) {
			console.error('Error updating production status:', err);
			alert('Erro ao atualizar status de produção: ' + (err?.message || 'Tente novamente'));
		} finally {
			setLoading(false);
		}
	}

	async function openOrderDetails(orderSummary) {
		const full = await orderService.fetchOrderDetails(orderSummary.id);
		setSelectedOrder(full);
	}
    
	async function handleCancelOrder(order) {
		if (!order) return;
		if (!window.confirm(`Cancelar o pedido ${order.code}?`)) return;
		setLoading(true);
		try {
			await orderService.cancelOrder(order.id);
			await loadData();
			// Refresh modal content if still open
			const updated = await orderService.fetchOrderDetails(order.id);
			setSelectedOrder(updated);
		} catch (err) {
			alert('Erro ao cancelar o pedido: ' + (err?.message || ''));
		} finally {
			setLoading(false);
		}
	}
	
	async function handleResetOrders() {
		if (!window.confirm('Resetar pedidos para dados mock? Isso apagará todos os pedidos salvos localmente.')) {
			return;
		}
		setLoading(true);
		try {
			await orderService.resetOrdersToMock();
			await loadData();
			alert('Pedidos resetados com sucesso!');
		} catch (err) {
			alert('Erro ao resetar pedidos: ' + err.message);
		} finally {
			setLoading(false);
		}
	}

	const showingOrders = activeTab === 'PEDIDOS';
	const listEmpty = showingOrders ? orders.length === 0 : productionItems.length === 0;

		return (
		<BackWithSidebar layout>
			<div className="max-w-5xl mx-auto">
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center gap-3">
						<h1 className="text-xl font-semibold text-gray-800">Pedidos de Mercadoria</h1>
						{isUsingFallback && (
							<span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-[10px] font-semibold rounded-md border border-yellow-300">
								MOCK
							</span>
						)}
					</div>
					<button
						onClick={() => setShowDebug(!showDebug)}
						className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1 rounded-lg border hover:bg-gray-50"
						title="Toggle debug mode"
					>
						{showDebug ? '🔧 Debug ON' : '⚙️ Debug'}
					</button>
				</div>				{error && (
					<div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
						<div className="flex items-center gap-2">
							<GeneralIcon iconName="alerta" className="w-5 h-5 text-red-600" />
							<p className="text-sm text-red-800">{error}</p>
						</div>
						<button
							onClick={() => setError(null)}
							className="text-red-600 hover:text-red-800 text-xs font-medium"
						>
							Dispensar
						</button>
					</div>
				)}
				
				{showDebug && (
					<div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
						<div className="flex items-center justify-between">
							<div>
								<div className="text-sm font-semibold text-yellow-800">Modo Debug</div>
								<div className="text-xs text-yellow-700">Resetar pedidos salvos localmente para dados mock</div>
							</div>
							<button
								onClick={handleResetOrders}
								className="px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 transition font-medium"
							>
								Resetar Pedidos
							</button>
						</div>
					</div>
				)}

				{/* Tabs */}
				<div className="flex gap-2 mb-4">
					<button
						onClick={() => setActiveTab('PEDIDOS')}
						className={`h-10 px-5 rounded-lg text-sm font-medium transition ${
							activeTab === 'PEDIDOS' ? 'bg-purple-600 text-white' : 'bg-white border text-gray-600'
						}`}
					>
						Pedidos
					</button>
					<button
						onClick={() => setActiveTab('PRODUCAO')}
						className={`h-10 px-5 rounded-lg text-sm font-medium transition ${
							activeTab === 'PRODUCAO' ? 'bg-purple-600 text-white' : 'bg-white border text-gray-600'
						}`}
					>
						Painel de Produção
					</button>
				</div>

				{/* Search + Filter */}
				<div className="flex items-center gap-2 mb-3">
					<div className="flex-1">
						<SearchBar
							value={search}
							onChange={e => setSearch(e.target.value)}
							placeholder={showingOrders ? 'Pesquisar por código' : 'Pesquisar produto'}
						/>
					</div>
					<div className="relative">
						<button
							onClick={() => setFiltersMenuOpen(o => !o)}
							className="h-10 px-4 rounded-lg border bg-white flex items-center gap-2 text-sm font-medium"
						>
							Filtrar
							<GeneralIcon iconName="filtrar" className="w-4 h-4" />
						</button>
						{filtersMenuOpen && (
							<div className="absolute right-0 mt-2 w-72 bg-white border rounded-xl shadow-lg z-10 p-4 space-y-4">
								{showingOrders ? (
									<div>
										<div className="text-xs font-semibold text-gray-500 mb-2">Status do pedido</div>
										<div className="flex flex-wrap gap-2">
											{ORDER_STATUSES.map(st => (
												<FilterChip
													key={st}
													label={st}
													active={orderStatusFilters.includes(st)}
													onClick={() => toggleOrderStatus(st)}
												/>
											))}
										</div>
									</div>
								) : (
									<div>
										<div className="text-xs font-semibold text-gray-500 mb-2">Status de produção</div>
										<div className="flex flex-wrap gap-2">
											{PRODUCTION_STATUSES.map(st => (
												<FilterChip
													key={st}
													label={st}
													active={prodStatusFilters.includes(st)}
													onClick={() => toggleProdStatus(st)}
												/>
											))}
										</div>
									</div>
								)}
								<div className="flex justify-end pt-2">
									<button onClick={clearFilters} className="text-xs text-gray-500 hover:text-gray-700">
										Limpar filtros
									</button>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Active filter chips below search */}
				<div className="flex flex-wrap gap-2 mb-4">
					{showingOrders && orderStatusFilters.map(st => (
						<ActiveChip key={st} label={st} onRemove={() => toggleOrderStatus(st)} />
					))}
					{!showingOrders && prodStatusFilters.map(st => (
						<ActiveChip key={st} label={st} onRemove={() => toggleProdStatus(st)} />
					))}
				</div>

				{/* Results header */}
				<div className="text-xs text-gray-500 mb-3">
					{loading ? 'Carregando...' : `Encontramos ${showingOrders ? orders.length : productionItems.length} resultados`}
				</div>

				{/* Content */}
				{loading ? (
					<div className="space-y-3">
						{showingOrders
							? [1, 2, 3].map(i => <OrderCardSkeleton key={i} />)
							: [1, 2, 3].map(i => <ProductionItemSkeleton key={i} />)}
					</div>
				) : listEmpty ? (
					<EmptyState production={!showingOrders} />
				) : (
					<div className="space-y-3">
						{showingOrders
							? orders.map(o => (
									<OrderCard key={o.id} order={o} onClick={openOrderDetails} />
								))
							: productionItems.map(i => (
									<ProductionItemCard key={i.id} item={i} onAdvance={handleAdvanceProduction} />
								))}
					</div>
				)}
			</div>
			{selectedOrder && (
				<OrderDetailsModal
					order={selectedOrder}
					onClose={() => setSelectedOrder(null)}
					onUpdateItem={handleAdvanceProduction}
					onCancelOrder={handleCancelOrder}
				/>
			)}
		</BackWithSidebar>
	);
}

function FilterChip({ label, active, onClick }) {
	return (
		<button
			onClick={onClick}
			className={`px-3 py-1.5 rounded-lg text-[11px] font-medium border transition ${
				active ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 hover:bg-gray-50'
			}`}
		>
			{label}
		</button>
	);
}

function ActiveChip({ label, onRemove }) {
	return (
		<div className="flex items-center gap-1 bg-purple-50 text-purple-600 px-3 py-1.5 rounded-lg text-[11px] font-medium">
			{label}
			<button onClick={onRemove} className="ml-1 text-purple-500 hover:text-purple-700">
				<span className="text-xs">×</span>
			</button>
		</div>
	);
}

